// Les briques d'interface partagées : boutons, chips, cases à cocher, champs,
// cartes, notifications.
//
// POURQUOI CE FICHIER EXISTE. Le socle `themeCss` unifiait déjà les COULEURS
// des deux apps, mais chacune redessinait ses composants par-dessus : Tailwind
// et `@apply` côté extension (`index.css`), CSS écrit à la main côté console
// (`app.css`). Deux grammaires pour la même marque — et surtout deux endroits
// où corriger la même chose. La divergence est arrivée : les pages de
// l'extension consommaient `--brand-blue` (#0a66c2, sans variante sombre) là où
// la console dérivait `--s-accent` (#4a9be8 en sombre). Le même bouton
// principal n'avait donc pas la même couleur dans le noir, sans qu'aucun test
// ni aucun build ne s'en plaigne.
//
// Exporté comme CHAÎNE, pour la raison de `previewCss` : l'extension injecte
// dans un shadow DOM (pas de feuille de style hôte à laquelle se raccrocher),
// la console dans une balise <style>.
//
// DEUX RÈGLES EN Y TOUCHANT :
//
//  1. Les couleurs viennent TOUTES de `--s-*`, jamais d'un littéral. C'est ce
//     qui rend le mode sombre gratuit et vérifiable (theme.test.ts).
//  2. La géométrie (rayons, épaisseurs) est écrite en littéral ici et nulle
//     part ailleurs. Elle ne peut pas passer par des variables : l'extension
//     pose les primitives dans un shadow-root où les `--r-*` de la page hôte
//     n'existent pas, et une variable absente retomberait silencieusement sur
//     `initial` — exactement la panne muette que ce paquet existe pour fermer.
//
// AUCUNE dépendance plateforme, conformément à la règle du paquet.
//
// DÉTAIL QUI PIÈGE : la feuille est un littéral de gabarit. Un backtick dans un
// commentaire CSS ferme la chaîne et casse le build sur une erreur de syntaxe
// qui désigne une ligne sans rapport. Citer les sélecteurs entre 'apostrophes'
// à l'intérieur de la chaîne — les backticks restent permis dans les
// commentaires TypeScript, au-dessus.

/**
 * Échelle typographique commune aux deux apps.
 *
 * Le plancher est 12px : en dessous, le texte cesse d'être lisible
 * confortablement et tombe sous le seuil que la review UX reprochait déjà à
 * l'extension. Les micro-libellés (en-têtes de tableau, tags) s'arrêtent donc à
 * `--s-text-xs`, et gagnent en graisse plutôt qu'en petitesse.
 */
const TYPO = `
  --s-text-xs: 12px;
  --s-text-sm: 13px;
  --s-text-base: 14px;
  --s-text-md: 15px;
  --s-text-lg: 17px;
  --s-text-xl: 20px;
  --s-text-2xl: 24px;
  --s-text-3xl: 32px;
`

export const primitivesCss = `
/* === Échelle typographique ======================================== */
:root, :host { ${TYPO} }

/* === Anneau de focus ==============================================
   'focus-visible' et pas 'focus' : invisible à la souris, toujours présent au
   clavier. On ne supprime jamais l'indicateur de focus. */
.focus-ring:focus-visible,
.btn:focus-visible,
.btn-primary:focus-visible,
.btn-secondary:focus-visible,
.chip:focus-visible,
.input-field:focus-visible {
  outline: 2px solid var(--s-accent);
  outline-offset: 2px;
}

/* === Boutons ======================================================
   Pilule pleine pour l'action principale, pilule contour pour les autres.
   Aucun déplacement au survol (pas de translateY) : le widget de l'extension
   n'en fait pas, et deux conventions de survol dans un même produit se
   remarquent. Seule la couleur bouge. */
.btn,
.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: var(--s-text-sm);
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color .15s cubic-bezier(.4,0,.2,1),
              color .15s cubic-bezier(.4,0,.2,1),
              border-color .15s cubic-bezier(.4,0,.2,1);
}
.btn { background: transparent; color: var(--s-ink-2); }
.btn:hover { background: var(--s-surface-2); color: var(--s-ink); }

/* APLAT et pas '--s-accent' : ce dernier est éclairci en sombre pour rester
   lisible en texte, ce qui ne laisse que 2,9:1 à du blanc posé dessus. */
.btn-primary { background: var(--s-accent-fill); color: var(--s-on-accent); }
.btn-primary:hover { background: var(--s-accent-fill-hover); }

/* '.btn-ghost' est le nom historique de la console, '.btn-secondary' celui de
   l'extension. Même bouton : on garde les deux plutôt que de réécrire les
   appels des deux côtés pour un renommage qui n'apporte rien. */
.btn-secondary,
.btn-ghost {
  background: transparent;
  color: var(--s-accent);
  border-color: var(--s-accent);
}
.btn-secondary:hover,
.btn-ghost:hover { background: var(--s-accent-tint); }

.btn-danger { background: transparent; color: var(--s-danger); border-color: var(--s-danger); }
.btn-danger:hover { background: var(--s-danger-tint); }

.btn-sm { padding: 6px 12px; font-size: var(--s-text-xs); }
.btn-lg { padding: 12px 22px; font-size: var(--s-text-md); }
.btn-icon { padding: 7px; border-radius: 8px; gap: 0; }

.btn[disabled],
.btn-primary[disabled],
.btn-secondary[disabled],
.btn-ghost[disabled],
.btn-danger[disabled] { opacity: .55; cursor: not-allowed; }

/* === Chips (filtres, étiquettes cliquables) ======================= */
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid var(--s-line-strong);
  background: var(--s-surface);
  color: var(--s-ink-2);
  font-family: inherit;
  font-size: var(--s-text-xs);
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color .15s cubic-bezier(.4,0,.2,1),
              border-color .15s cubic-bezier(.4,0,.2,1);
}
.chip:hover { background: var(--s-surface-2); }
.chip.on {
  background: var(--s-accent-tint);
  border-color: var(--s-accent-border);
  color: var(--s-accent);
}

/* === Cases à cocher ===============================================
   'accent-color' plutôt qu'une case redessinée : le natif reste accessible,
   annonçable par les lecteurs d'écran et cochable au clavier sans un octet de
   JavaScript. La taille est portée à 16px pour rester cliquable. */
.checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--s-accent);
  cursor: pointer;
  flex: none;
  margin: 0;
}

/* === Champs de saisie ============================================= */
.input-field {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--s-line-strong);
  border-radius: 8px;
  background: var(--s-surface);
  color: var(--s-ink);
  font-family: inherit;
  font-size: var(--s-text-base);
  transition: box-shadow .15s cubic-bezier(.4,0,.2,1),
              border-color .15s cubic-bezier(.4,0,.2,1);
}
.input-field::placeholder { color: var(--s-ink-3); }
.input-field:focus {
  outline: none;
  border-color: transparent;
  box-shadow: 0 0 0 2px var(--s-accent);
}

.field-label {
  display: block;
  font-size: var(--s-text-xs);
  font-weight: 700;
  color: var(--s-ink-2);
  margin-bottom: 5px;
}
/* Le texte d'aide est SOUS le champ et toujours visible : un placeholder qui
   disparaît à la première frappe n'est pas une étiquette. */
.field-help {
  font-size: var(--s-text-xs);
  color: var(--s-ink-3);
  margin-top: 4px;
  line-height: 1.45;
}
.field-error {
  font-size: var(--s-text-xs);
  color: var(--s-danger);
  margin-top: 4px;
  font-weight: 600;
}

/* === Cartes ======================================================= */
.card {
  background: var(--s-surface);
  border: 1px solid var(--s-line);
  border-radius: 12px;
  overflow: hidden;
}

/* === Notifications ================================================
   Bordure de gauche colorée plutôt qu'un fond teinté : lisible dans les deux
   thèmes sans recalculer un contraste de texte sur fond de couleur. */
.toast {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  background: var(--s-surface);
  border: 1px solid var(--s-line-strong);
  border-left: 3px solid var(--s-accent);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: var(--s-text-base);
  color: var(--s-ink);
  max-width: 380px;
  box-shadow: 0 6px 16px rgba(0,0,0,.14), 0 2px 5px rgba(0,0,0,.08);
  animation: s-slidein .25s cubic-bezier(.4,0,.2,1);
}
@keyframes s-slidein { from { transform: translateY(8px); opacity: 0 } to { transform: none; opacity: 1 } }
.toast.ok { border-left-color: var(--s-good); }
.toast.err { border-left-color: var(--s-danger); }
.toast-close {
  border: 0;
  background: none;
  color: var(--s-ink-3);
  cursor: pointer;
  padding: 0 0 0 4px;
  font-family: inherit;
  font-size: var(--s-text-base);
  line-height: 1;
}
.toast-close:hover { color: var(--s-ink); }

/* === Indicateur d'attente ========================================= */
.spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, currentColor 25%, transparent);
  border-top-color: currentColor;
  animation: s-spin .7s linear infinite;
  display: inline-block;
  vertical-align: -3px;
  flex: none;
}
.spinner.big { width: 26px; height: 26px; }
@keyframes s-spin { to { transform: rotate(360deg) } }

/* === Bascule de thème =============================================
   Trois états et non deux : « auto » n'est pas la même chose que « clair ».
   Sans lui, l'utilisateur qui suit son OS ne peut plus y revenir une fois
   qu'il a touché au réglage. */
.themetoggle {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  border-radius: 999px;
  background: var(--s-surface-2);
  border: 1px solid var(--s-line);
}
.themetoggle button {
  display: grid;
  place-items: center;
  width: 26px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: none;
  color: var(--s-ink-3);
  cursor: pointer;
  transition: background-color .15s cubic-bezier(.4,0,.2,1),
              color .15s cubic-bezier(.4,0,.2,1);
}
.themetoggle button:hover { color: var(--s-ink); }
.themetoggle button[aria-pressed="true"] {
  background: var(--s-surface);
  color: var(--s-accent);
  box-shadow: 0 1px 2px rgba(0,0,0,.10);
}
.themetoggle button:focus-visible {
  outline: 2px solid var(--s-accent);
  outline-offset: 1px;
}

/* === Mouvement réduit =============================================
   Une seule déclaration pour les deux apps. Le spinner est EXCLU : le couper
   laisserait un disque immobile pendant les quinze secondes d'un appel IA,
   c'est-à-dire un écran qui a l'air planté. Respecter la préférence, ce n'est
   pas supprimer le retour d'information. */
@media (prefers-reduced-motion: reduce) {
  *:not(.spinner) {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
    scroll-behavior: auto !important;
  }
  .spinner { animation-duration: 1.6s; }
}
`
